// src/components/board/Board.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import axios, { markAllTasksAsViewed } from "../../api/axiosConfig";
import { subscribeToBoard, unsubscribeFromBoard } from "../../api/socketService";
import {
  FaSearch,
  FaTimes,
  FaCheckDouble,
  FaChevronDown,
  FaChevronRight,
  FaPen,
} from "react-icons/fa";
import TaskItem from "./TaskItem";
import CollapsibleSection from "../common/CollapsibleSection";
import Toast from "../common/Toast";
import { getInitials, getAvatarColor } from "../../utils/avatar";
// ⬇︎ NEU
import CreateTaskModal from "../tasks/CreateTaskModal";

const PRIORITY_TEXT = ["low", "medium", "high", "highest"];

function Board({ board, currentUser, onMembersChanged, allBoards = [], onTaskMoved }) {
  const [tasks, setTasks] = useState(board.tasks || []);
  const [statuses, setStatuses] = useState(
    board.statuses || ["offen", "in Bearbeitung", "Done"]
  );
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [newTaskDetails, setNewTaskDetails] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [coAdmins, setCoAdmins] = useState(board.co_admins || []);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsedStatuses, setCollapsedStatuses] = useState(() => {
    try {
      const saved = localStorage.getItem(`collapsed-statuses-${board.id}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Toast-State
  const [toastMsg, setToastMsg] = useState("");

  // ⬇︎ NEU: Modal-State
  const [isCreateOpen, setCreateOpen] = useState(false);

  // Rollen-Dropdown
  const [rolesOpen, setRolesOpen] = useState(false);
  const rolesRef = useRef(null);

  // Board-Prop -> lokalen State synchronisieren
  useEffect(() => {
    setTasks(board.tasks || []);
    setStatuses(board.statuses || ["offen", "in Bearbeitung", "Done"]);
    setCoAdmins(board.co_admins || []);
  }, [board]);

  // Alle User laden
  useEffect(() => {
    axios
      .get("/users")
      .then((res) => setAllUsers(res.data || []))
      .catch(console.error);
  }, []);

  // Echtzeit-Sync via Socket.IO
  useEffect(() => {
    subscribeToBoard(board.id, {
      onTaskCreated: (newTask) => {
        setTasks((prev) => {
          if (prev.find((t) => t.id === newTask.id)) return prev;
          return [...prev, newTask];
        });
        setToastMsg("✨ Neue Task erstellt");
      },
      onTaskUpdated: (updatedTask) => {
        setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
      },
      onTaskDeleted: ({ id }) => {
        setTasks((prev) => prev.filter((t) => t.id !== id));
      },
      onCommentCreated: () => setToastMsg("💬 Neuer Kommentar"),
      onAttachmentCreated: () => setToastMsg("📎 Neuer Anhang"),
    });

    return () => unsubscribeFromBoard(board.id);
  }, [board.id]);

  // Klick außerhalb schließt das Rollen-Dropdown
  useEffect(() => {
    if (!rolesOpen) return;
    const onDocClick = (e) => {
      if (rolesRef.current && !rolesRef.current.contains(e.target)) {
        setRolesOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [rolesOpen]);

  const boardUsers = useMemo(() => {
    const invited = Array.isArray(board.invited_users)
      ? board.invited_users
      : [];
    // IDs können als Zahl oder String kommen → alles auf String normalisieren
    const toId = (v) => (v == null ? null : String(v));

    const ids = new Set(invited.map(toId));
    const adminId = toId(board.admin);
    if (adminId) ids.add(adminId);
    (board.co_admins || []).forEach((id) => ids.add(toId(id)));
    (board.tasks || []).forEach((t) => t?.assignee && ids.add(toId(t.assignee)));

    return allUsers.filter((u) => ids.has(toId(u.id)));
  }, [board.invited_users, board.admin, board.co_admins, board.tasks, allUsers]);

  const userById = useMemo(() => {
    const map = new Map();
    boardUsers.forEach((u) => {
      map.set(String(u.id), u);
    });
    return map;
  }, [boardUsers]);

  const normalizedSearch = (searchTerm || "").trim().toLowerCase();

  // Drag & Drop: Tasks verschieben
  const onTaskDragEnd = async (result) => {
    const { draggableId, source, destination } = result;
    if (!destination) return;
    
    // Falls Task nicht wirklich verschoben wurde
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const movedTask = tasks.find((t) => String(t.id) === String(draggableId));
    if (!movedTask) return;

    const sourceStatus = source.droppableId;
    const destStatus = destination.droppableId;

    // Reordere Tasks lokal
    const prevTasks = tasks;
    let newTasks = Array.from(tasks);

    // Entferne Task aus Quelle
    newTasks = newTasks.filter((t) => String(t.id) !== String(draggableId));

    // Finde Index in Ziel-Spalte und füge dort ein
    const tasksInDest = newTasks.filter((t) => t.status === destStatus);
    const insertIndex = tasksInDest.length > 0 
      ? newTasks.findIndex((t) => t.status === destStatus && t.id === tasksInDest[destination.index - 1]?.id) + 1
      : newTasks.findIndex((t) => t.status === destStatus);

    const updatedTask = { ...movedTask, status: destStatus };
    if (insertIndex >= 0) {
      newTasks.splice(insertIndex, 0, updatedTask);
    } else {
      newTasks.push(updatedTask);
    }

    setTasks(newTasks);

    // Speichere auf Backend
    try {
      await axios.put(`/boards/${board.id}/tasks/${draggableId}`, {
        title: movedTask.title,
        details: movedTask.details ?? "",
        assignee: movedTask.assignee ?? null,
        status: destStatus,
        priority: movedTask.priority ?? 0,
        labels: movedTask.labels ?? [],
      });
    } catch (err) {
      console.error(err);
      setTasks(prevTasks);
    }
  };

  // Drag & Drop: Status-Reihenfolge
  const onStatusDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const newStatusesOrder = Array.from(statuses);
    const [removed] = newStatusesOrder.splice(source.index, 1);
    newStatusesOrder.splice(destination.index, 0, removed);
    setStatuses(newStatusesOrder);
    axios
      .put(`/boards/${board.id}/statuses`, { statuses: newStatusesOrder })
      .then((res) => setStatuses(res.data.statuses))
      .catch(console.error);
  };

  const handleDeleteStatus = async (e, statusToDelete) => {
    e.stopPropagation();
    if (statuses.length <= 1) return;
    const updatedStatuses = statuses.filter((s) => s !== statusToDelete);
    const defaultStatus = updatedStatuses[0];
    const updatedTasks = tasks.map((task) =>
      task.status === statusToDelete ? { ...task, status: defaultStatus } : task
    );
    setStatuses(updatedStatuses);
    setTasks(updatedTasks);
    try {
      const res = await axios.put(`/boards/${board.id}/statuses`, {
        statuses: updatedStatuses,
      });
      setStatuses(res.data.statuses);
    } catch (err) {
      console.error(err);
    }
  };

  // Task anlegen (Quick-Form)
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    try {
      await axios.post(`/boards/${board.id}/tasks`, {
        title: newTaskTitle.trim(),
        status: statuses[0],
        details: newTaskDetails || "",
        priority: 0, // Default Low
        labels: [],  // Default keine
      });
      // Task wird via Socket-Event hinzugefügt (onTaskCreated)
      setNewTaskTitle("");
      setNewTaskDetails("");
      setToastMsg("Task erfolgreich erstellt ✅");
    } catch (err) {
      console.error(err);
    }
  };

  // ⬇︎ NEU: Callback wenn im Modal erstellt wurde
  const handleCreated = (t) => {
    // Task wird via Socket-Event hinzugefügt (onTaskCreated)
    setToastMsg("Task erfolgreich erstellt ✅");
  };

  // Alle Tasks als gelesen markieren
  const handleMarkAllAsViewed = async () => {
    try {
      await markAllTasksAsViewed(board.id);
      setToastMsg("Alle Tasks als gelesen markiert ✅");
    } catch (err) {
      console.error(err);
      setToastMsg("Fehler beim Markieren der Tasks ❌");
    }
  };

  // Toggle Spalte collapse/expand
  const toggleStatusCollapse = (status) => {
    setCollapsedStatuses((prev) => {
      const updated = { ...prev, [status]: !prev[status] };
      localStorage.setItem(`collapsed-statuses-${board.id}`, JSON.stringify(updated));
      return updated;
    });
  };

  // Neuen Status hinzufügen
  const handleAddStatus = async (e) => {
    e.preventDefault();
    const trimmedStatus = newStatus.trim();
    if (!trimmedStatus) return;
    if (!statuses.includes(trimmedStatus)) {
      const updatedStatuses = [...statuses, trimmedStatus];
      try {
        const res = await axios.put(`/boards/${board.id}/statuses`, {
          statuses: updatedStatuses,
        });
        setStatuses(res.data.statuses);
      } catch (err) {
        console.error(err);
      }
    }
    setNewStatus("");
  };

  // Task aktualisiert (kommt von TaskItem)
  const handleTaskUpdated = (updatedTask) => {
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
    setToastMsg("Task erfolgreich gespeichert 💾");
  };

  // Task gelöscht (kommt von TaskItem)
  const handleTaskDeleted = (deletedTaskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== deletedTaskId));
    // Optional: setToastMsg("Task gelöscht 🗑️");
  };

  const handleTaskMoved = ({ task, toBoardId }) => {
    // Lokal entfernen, globale Aktualisierung anstoßen
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
    onTaskMoved?.({ task, fromBoardId: board.id, toBoardId });
    setToastMsg("Task in anderes Board verschoben ↗️");
  };

  // Co-Admin toggeln
  const toggleCoAdmin = async (userId) => {
    if (coAdmins.includes(userId)) {
      try {
        const res = await axios.delete(`/boards/${board.id}/coadmin`, {
          data: { coAdminId: userId },
        });
        setCoAdmins(res.data.co_admins);
        onMembersChanged && onMembersChanged();
      } catch (err) {
        console.error(err);
        alert("Co-Admin konnte nicht entfernt werden.");
      }
    } else {
      try {
        const res = await axios.put(`/boards/${board.id}/coadmin`, {
          coAdminId: userId,
        });
        setCoAdmins(res.data.co_admins);
        onMembersChanged && onMembersChanged();
      } catch (err) {
        console.error(err);
        alert("Co-Admin konnte nicht hinzugefügt werden.");
      }
    }
  };

  // Berechtigungen
  const iAmAdmin = board.admin === currentUser.id;
  const canEdit =
    iAmAdmin || (Array.isArray(coAdmins) && coAdmins.includes(currentUser.id));

  const canRemoveMember = (targetId) => {
    if (!canEdit) return false;
    if (targetId === board.admin) return false;
    const targetIsCoAdmin = (coAdmins || []).includes(targetId);
    if (!iAmAdmin && targetIsCoAdmin) return false;
    return true;
  };

  const removeMember = async (user) => {
    if (!canRemoveMember(user.id)) return;
    const label = user.name || user.email || `#${user.id}`;
    if (
      !window.confirm(
        `Willst du den Benutzer „${label}“ wirklich vom Board entfernen?`
      )
    )
      return;
    try {
      await axios.delete(`/boards/${board.id}/members/${user.id}`);
      setCoAdmins((prev) => prev.filter((id) => id !== user.id));
      onMembersChanged && onMembersChanged();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || "Konnte Nutzer nicht entfernen.");
    }
  };

  const meLabel = currentUser?.name || currentUser?.email || "Du";

  return (
    <section className="board">
      {/* Header */}
      <div className="board-toolbar">
        <h2 className="board-title" title={board.name}>
          {board.name}
        </h2>

        <div className="board-search">
          <FaSearch className="board-search-icon" aria-hidden />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tasks durchsuchen..."
            aria-label="Tasks durchsuchen"
            className="input"
          />
          {searchTerm && (
            <button
              type="button"
              className="icon-btn board-search-clear"
              onClick={() => setSearchTerm("")}
              title="Suchfeld leeren"
              aria-label="Suchfeld leeren"
            >
              <FaTimes />
            </button>
          )}
        </div>

        <div className="board-toolbar-actions">
          {/* Mark all as viewed button */}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleMarkAllAsViewed}
            title="Markiere alle Tasks dieser View als gelesen"
          >
            <FaCheckDouble /> Alle als gelesen
          </button>

          <div className="members-menu" ref={rolesRef}>
            <button
              type="button"
              onClick={() => setRolesOpen((v) => !v)}
              title="Mitglieder & Rollen"
              aria-expanded={rolesOpen}
              className="btn btn-secondary members-trigger"
            >
              <span
                aria-hidden
                className="avatar avatar-sm"
                style={{ background: getAvatarColor(currentUser?.id) }}
              >
                {getInitials(meLabel)}
              </span>
              <span className="members-trigger-name">{meLabel}</span>
            </button>

            {rolesOpen && (
              <div className="members-popover">
                <div className="members-popover-head">Mitglieder & Rollen</div>

                {!canEdit && (
                  <div className="members-popover-note">
                    Du kannst Rollen ansehen, aber nicht ändern.
                  </div>
                )}

                <div className="members-list">
                  {boardUsers.length === 0 ? (
                    <div className="members-empty">Keine Mitglieder gefunden.</div>
                  ) : (
                    boardUsers.map((u) => {
                      const isAdmin = u.id === board.admin;
                      const isCo = coAdmins.includes(u.id);
                      return (
                        <div key={u.id} className="member-row">
                          <span
                            aria-hidden
                            className="avatar avatar-sm"
                            style={{ background: getAvatarColor(u.id) }}
                          >
                            {getInitials(u.name || u.email)}
                          </span>
                          <div className="member-info">
                            <div className="member-name">{u.name || u.email}</div>
                            <div className="member-email">{u.email}</div>
                          </div>

                          {isAdmin ? (
                            <span className="member-role">Admin</span>
                          ) : (
                            <div className="member-controls">
                              <label
                                className={`member-coadmin ${canEdit ? "" : "is-disabled"}`}
                              >
                                <input
                                  type="checkbox"
                                  disabled={!canEdit}
                                  checked={isCo}
                                  onChange={() => toggleCoAdmin(u.id)}
                                />
                                Co-Admin
                              </label>

                              {canRemoveMember(u.id) && (
                                <button
                                  className="btn btn-danger-ghost btn-sm"
                                  onClick={() => removeMember(u)}
                                  title="Vom Board entfernen"
                                >
                                  Entfernen
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="board-actions">
        <CollapsibleSection title="Aufgabe hinzufügen">
          {/* Quick-Form bleibt bestehen */}
          <form onSubmit={handleCreateTask}>
            <input
              className="input-field"
              type="text"
              placeholder="Neue Aufgabe..."
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              autoFocus
            />
            <textarea
              className="input-field"
              placeholder="Beschreibung (optional)"
              value={newTaskDetails}
              onChange={(e) => setNewTaskDetails(e.target.value)}
              rows={2}
            />
            <div className="form-actions">
              <button className="btn" type="submit">
                Aufgabe hinzufügen
              </button>
              {/* ⬇︎ Zusatz-Button für das Modal */}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCreateOpen(true)}
                title="Aufgabe mit mehr Optionen erstellen"
              >
                Erweitert…
              </button>
            </div>
          </form>
        </CollapsibleSection>

        {canEdit && (
          <button
            className={`btn ${editMode ? "" : "btn-secondary"}`}
            onClick={() => setEditMode(!editMode)}
          >
            <FaPen />
            <span className="label-long">
              {editMode
                ? "Bearbeitungsmodus beenden"
                : "Bearbeitungsmodus aktivieren"}
            </span>
            <span className="label-short">{editMode ? "Fertig" : "Bearbeiten"}</span>
          </button>
        )}
      </div>

      {editMode && (
        <div className="status-editor">
          <h3 className="status-editor-title">Statusreihenfolge bearbeiten</h3>
          <p className="status-editor-hint">Spalten per Drag & Drop umsortieren.</p>
          <DragDropContext onDragEnd={onStatusDragEnd}>
            <Droppable droppableId="statuses" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="statuses-container"
                >
                  {statuses.map((status, index) => (
                    <Draggable key={status} draggableId={status} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="status-item"
                        >
                          {status}
                          {statuses.length > 1 && (
                            <button
                              className="status-item-delete"
                              title={`Status „${status}“ entfernen`}
                              aria-label={`Status „${status}“ entfernen`}
                              onClick={(e) => handleDeleteStatus(e, status)}
                            >
                              <FaTimes />
                            </button>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <CollapsibleSection title="Neuen Status hinzufügen">
            <form onSubmit={handleAddStatus} className="inline-form">
              <input
                className="input-field"
                type="text"
                placeholder="Neuer Status..."
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                autoFocus
              />
              <button className="btn" type="submit">
                Status hinzufügen
              </button>
            </form>
          </CollapsibleSection>
        </div>
      )}

      <DragDropContext onDragEnd={onTaskDragEnd}>
        <div className="columns-container">
          {statuses.map((status) => {
            const tasksForStatus = tasks
              .filter((task) => {
                if (task.status !== status) return false;
                if (!normalizedSearch) return true;
                const assignee = userById.get(String(task.assignee));
                const prioLabel = PRIORITY_TEXT[task.priority] || "";
                const haystack = [
                  task.title,
                  task.details,
                  (task.labels || []).join(" "),
                  assignee?.name,
                  assignee?.email,
                  prioLabel,
                  String(task.priority ?? ""),
                  String(task.id),
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return haystack.includes(normalizedSearch);
              })
              .sort((a, b) => (b?.priority ?? 0) - (a?.priority ?? 0));

            const isCollapsed = !!collapsedStatuses[status];

            return (
              <Droppable key={status} droppableId={status}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`task-droppable ${snapshot.isDraggingOver ? "is-over" : ""}`}
                  >
                    <div className="column-header">
                      <h3 className="column-title" title={status}>
                        {status}
                      </h3>
                      <span className="column-count">{tasksForStatus.length}</span>
                      {tasksForStatus.length > 0 && (
                        <button
                          className="icon-btn column-collapse"
                          onClick={() => toggleStatusCollapse(status)}
                          title={isCollapsed ? "Ausklappen" : "Einklappen"}
                          aria-label={isCollapsed ? "Spalte ausklappen" : "Spalte einklappen"}
                          aria-expanded={!isCollapsed}
                        >
                          {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                        </button>
                      )}
                    </div>

                    {!isCollapsed && tasksForStatus.map((task, index) => (
                      <Draggable
                        key={String(task.id)}
                        draggableId={String(task.id)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "is-dragging" : undefined}
                          >
                            <TaskItem
                              task={task}
                              boardId={board.id}
                              boardUsers={boardUsers}
                              availableBoards={allBoards}
                              onTaskUpdated={handleTaskUpdated}
                              onTaskDeleted={handleTaskDeleted}
                              onTaskMoved={handleTaskMoved}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}

                    {tasksForStatus.length === 0 && !snapshot.isDraggingOver && (
                      <div className="column-empty">
                        {normalizedSearch ? "Keine Treffer" : "Keine Aufgaben"}
                      </div>
                    )}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      {/* Toast */}
      <Toast message={toastMsg} onClose={() => setToastMsg("")} />

      {/* ⬇︎ NEU: CreateTaskModal */}
      <CreateTaskModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        boardId={board?.id}
        statuses={statuses}
        members={boardUsers}
        defaultStatus={statuses?.[0] || ""}
        onCreated={handleCreated}
      />
    </section>
  );
}

export default Board;
